import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Plus, Trash2, Edit2, CalendarDays, Repeat, Building2, Ban } from 'lucide-react';
import { useDialog } from '@/context/DialogContext';
import api from '@/lib/api';

const HOLIDAY_TYPES = [
  { key: 'public',   label: 'Public',   color: '#16a34a' },
  { key: 'optional', label: 'Optional', color: '#0891b2' },
  { key: 'company',  label: 'Company',  color: '#7c3aed' },
];

const emptyForm = () => ({
  name: '',
  date: '',
  type: 'public',
  description: '',
  recurring_yearly: false,
  departments: [],
  excluded_departments: [],
});

export default function HolidaysTab() {
  const { confirm } = useDialog();
  const currentYear = new Date().getFullYear();

  const [holidays, setHolidays] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [year, setYear] = useState(currentYear);
  const [filterDept, setFilterDept] = useState('all');
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [hRes, dRes] = await Promise.all([
        api.get('/holidays', { params: { year, scope: 'all' } }),
        api.get('/departments'),
      ]);
      setHolidays(hRes.data || []);
      setDepartments(dRes.data || []);
    } catch (err) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [year]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({
      name: h.name || '',
      date: h.date || '',
      type: h.type || 'public',
      description: h.description || '',
      recurring_yearly: !!h.recurring_yearly,
      departments: h.departments || [],
      excluded_departments: h.excluded_departments || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Holiday name is required');
    if (!form.date) return toast.error('Date is required');
    try {
      if (editing) {
        await api.patch(`/holidays/${editing.id}`, form);
        toast.success('Holiday updated');
      } else {
        await api.post('/holidays', form);
        toast.success('Holiday created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save holiday';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save holiday');
    }
  };

  const remove = async (h) => {
    const ok = await confirm({
      title: `Delete "${h.name}"?`,
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success('Holiday deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete holiday');
    }
  };

  const toggleDeptChip = (deptList, dept) => {
    return deptList.includes(dept)
      ? deptList.filter(d => d !== dept)
      : [...deptList, dept];
  };

  const filtered = useMemo(() => {
    if (filterDept === 'all') return holidays;
    if (filterDept === 'global') return holidays.filter(h => (h.departments || []).length === 0);
    return holidays.filter(h => {
      const scoped = h.departments || [];
      const excl = h.excluded_departments || [];
      if (scoped.length) return scoped.includes(filterDept);
      return !excl.includes(filterDept);
    });
  }, [holidays, filterDept]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Holidays</h2>
          <p className="text-sm text-muted-foreground">Create org-wide or per-department holidays. Users see the holidays that apply to their department.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              <SelectItem value="global">Global only</SelectItem>
              {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="gap-1"><Plus className="w-4 h-4" /> Add Holiday</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-50" />
            No holidays configured for {year}. Click &quot;Add Holiday&quot; to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(h => {
            const typeCfg = HOLIDAY_TYPES.find(t => t.key === h.type) || HOLIDAY_TYPES[0];
            const isGlobal = !(h.departments || []).length;
            return (
              <Card key={h.id}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex-shrink-0 text-center px-3 py-2 border rounded">
                      <div className="text-xs uppercase text-muted-foreground">
                        {new Date(h.date).toLocaleString(undefined, { month: 'short' })}
                      </div>
                      <div className="text-2xl font-bold leading-none">
                        {new Date(h.date).getDate()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{h.name}</span>
                        <Badge style={{ backgroundColor: typeCfg.color + '22', color: typeCfg.color, border: `1px solid ${typeCfg.color}44` }}>{typeCfg.label}</Badge>
                        {h.recurring_yearly && (
                          <Badge variant="outline" className="gap-1"><Repeat className="w-3 h-3" /> Yearly</Badge>
                        )}
                        {isGlobal ? (
                          <Badge variant="outline" className="gap-1"><Building2 className="w-3 h-3" /> Global</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Applies to: {(h.departments || []).join(', ')}</span>
                        )}
                        {isGlobal && (h.excluded_departments || []).length > 0 && (
                          <span className="text-xs text-red-600 flex items-center gap-1"><Ban className="w-3 h-3" /> Excluded: {(h.excluded_departments || []).join(', ')}</span>
                        )}
                      </div>
                      {h.description && <div className="text-sm text-muted-foreground mt-1 truncate">{h.description}</div>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(h)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(h)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Holiday' : 'New Holiday'}</DialogTitle>
            <DialogDescription>
              Global holidays apply to everyone unless a department is listed under &quot;Excluded departments&quot;.
              Departmental holidays apply only to the selected departments.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Independence Day" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HOLIDAY_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={form.recurring_yearly} onCheckedChange={(v) => setForm({ ...form, recurring_yearly: v })} />
              <span className="text-sm">Repeats every year on the same month/day</span>
            </label>

            <div className="pt-2">
              <Label>Applies to (departments)</Label>
              <p className="text-xs text-muted-foreground mb-2">Leave empty for a global holiday that applies to everyone.</p>
              <div className="flex flex-wrap gap-2">
                {departments.length === 0 && <span className="text-xs text-muted-foreground">No departments yet — create users with departments first, or leave global.</span>}
                {departments.map(d => {
                  const on = form.departments.includes(d);
                  return (
                    <button type="button" key={d}
                      onClick={() => setForm({ ...form, departments: toggleDeptChip(form.departments, d), excluded_departments: form.excluded_departments.filter(x => x !== d) })}
                      className={`text-xs px-2 py-1 rounded border ${on ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-foreground'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>

            {form.departments.length === 0 && departments.length > 0 && (
              <div className="pt-1">
                <Label>Excluded from (global holidays only)</Label>
                <p className="text-xs text-muted-foreground mb-2">Selected departments will NOT get this holiday.</p>
                <div className="flex flex-wrap gap-2">
                  {departments.map(d => {
                    const on = form.excluded_departments.includes(d);
                    return (
                      <button type="button" key={d}
                        onClick={() => setForm({ ...form, excluded_departments: toggleDeptChip(form.excluded_departments, d) })}
                        className={`text-xs px-2 py-1 rounded border ${on ? 'bg-red-600 text-white border-red-600' : 'bg-white text-foreground'}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save}>{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
